"use client";

import {Partial} from "@/components/partial/partial";
import {useNeoContext} from "@/context/neo/neo.context";
import {PartialPageConfig} from "@/libs/config/navigation.config";
import {URLActions} from "@/libs/consts/common.const";
import {ActionType} from "@/libs/enums/common";
import {canAccessCrudAction, canReadModule} from "@/libs/helper";
import type {ReactNode} from "react";

export type CrudRouteParams = {
  id: string;
  action: string;
  slug: string;
};

type CrudRouteGuardProps = {
  params: CrudRouteParams;
  allowedSlugs: string[];
  listPath: string;
  moduleCode: string;
  renderManage: (params: CrudRouteParams) => ReactNode;
  renderView: (params: CrudRouteParams) => ReactNode;
};

export const CrudRouteGuard = ({
  params,
  allowedSlugs,
  listPath,
  moduleCode,
  renderManage,
  renderView
}: CrudRouteGuardProps) => {
  const {roleAccess} = useNeoContext();
  const isManageAction =
    params.action === ActionType.CREATE || params.action === ActionType.EDIT;
  const isValidRoute =
    Boolean(params?.id) &&
    URLActions.includes(params.action) &&
    allowedSlugs.includes(params.slug);

  if (!isValidRoute) {
    const config = PartialPageConfig.notFound;

    return (
      <Partial
        head={config.head}
        message={config.message}
        imageUrl={config.imageUrl}
        btnText={config.btnText}
        btnUrl={config.btnUrl}
      />
    );
  }

  if (!canReadModule(roleAccess, moduleCode)) {
    const config = PartialPageConfig.accessDenied;

    return (
      <Partial
        head={config.head}
        message={config.message}
        imageUrl={config.imageUrl}
        btnText="Back to home"
        btnUrl={config.btnUrl}
      />
    );
  }

  if (
    isManageAction &&
    !canAccessCrudAction(roleAccess, moduleCode, params.action)
  ) {
    const config = PartialPageConfig.accessDenied;

    return (
      <Partial
        head={config.head}
        message={config.message}
        imageUrl={config.imageUrl}
        btnText={config.btnText}
        btnUrl={listPath}
      />
    );
  }

  return <>{isManageAction ? renderManage(params) : renderView(params)}</>;
};
